import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { adminService } from '../../services/adminService'
import AdminLayout from '../../components/admin/AdminLayout'
import { devError } from '../../utils/devLog'
import Button from '../../components/ui/Button'

const STATUS_OPTIONS = ['pending', 'reviewing', 'editing', 'completed', 'delivered', 'cancelled']
const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  reviewing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  editing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  delivered: 'bg-pitch-green/20 text-pitch-green border-pitch-green/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
}

export default function AdminSubmissionDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [submission, setSubmission] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [adminNotes, setAdminNotes] = useState('')
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    fetchSubmission()
  }, [id])

  useEffect(() => {
    if (submission) {
      setStatus(submission.status)
      setAdminNotes(submission.adminNotes || '')
    }
  }, [submission])

  const fetchSubmission = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminService.getSubmission(id)
      if (response.success) {
        setSubmission(response.submission)
      } else {
        setError(response.error || 'Failed to fetch submission')
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch submission. Please try again.')
      devError('Error fetching submission', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async () => {
    if (!submission || status === submission.status) return
    try {
      setSaving(true)
      setError(null)
      const response = await adminService.updateSubmissionStatus(id, status)
      if (response.success) {
        setSubmission((s) => (s ? { ...s, status } : s))
        setSuccess('Status updated')
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(response.error || 'Failed to update status')
      }
    } catch (err) {
      setError(err.message || 'Failed to update status')
      devError('Error updating status', err)
    } finally {
      setSaving(false)
    }
  }

  const handleNotesSave = async () => {
    if (!submission) return
    try {
      setSaving(true)
      setError(null)
      const response = await adminService.updateSubmissionNotes(id, adminNotes)
      if (response.success) {
        setSubmission((s) => (s ? { ...s, adminNotes } : s))
        setSuccess('Notes saved')
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(response.error || 'Failed to save notes')
      }
    } catch (err) {
      setError(err.message || 'Failed to save notes')
      devError('Error saving notes', err)
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-400">Loading submission...</div>
      </AdminLayout>
    )
  }

  if (error && !submission) {
    return (
      <AdminLayout>
        <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex flex-wrap items-center justify-between gap-3">
          <span className="flex-1 min-w-0">{error}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchSubmission}>
              Retry
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/submissions')}>
              Back to Submissions
            </Button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  if (!submission) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-400">Submission not found</div>
        <Button onClick={() => navigate('/admin/submissions')} variant="outline">
          Back to Submissions
        </Button>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-6">
          <Button onClick={() => navigate('/admin/submissions')} variant="ghost" size="sm">
            ← Back to Submissions
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-black mb-2">
            <span className="text-white">Submission Details</span>
          </h1>
          <p className="text-gray-400">View and manage this submission</p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex flex-wrap items-center justify-between gap-3">
            <span className="flex-1 min-w-0">{error}</span>
            <Button variant="outline" size="sm" onClick={fetchSubmission}>
              Retry
            </Button>
          </div>
        )}
        {success && (
          <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        <div className="space-y-6">
          {/* Customer & submission info */}
          <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Submission Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Name</p>
                <p className="text-white font-semibold">{submission.name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Email</p>
                <p className="text-white">{submission.email}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Phone</p>
                <p className="text-white">{submission.phone}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Match Type</p>
                <p className="text-white">{submission.matchType}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Focus</p>
                <p className="text-white">{submission.focus}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Video Hours</p>
                <p className="text-white">{submission.videoHours}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Plan</p>
                <p className="text-white">{submission.planId}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Submitted</p>
                <p className="text-gray-400 text-sm">{formatDate(submission.createdAt)}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-gray-500 text-sm mb-1">Google Drive Link</p>
                {submission.driveLink ? (
                  <a
                    href={submission.driveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pitch-green hover:text-neon-green break-all"
                  >
                    {submission.driveLink}
                  </a>
                ) : (
                  <span className="text-gray-500">Not provided</span>
                )}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Status</h2>
            <div className="flex flex-wrap items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-sm font-bold border ${STATUS_COLORS[submission.status] || STATUS_COLORS.pending}`}>
                {submission.status.toUpperCase()}
              </span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Update submission status"
                className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <Button onClick={handleStatusChange} disabled={saving || status === submission.status} loading={saving} size="sm">
                Update Status
              </Button>
            </div>
          </div>

          {/* Admin notes */}
          <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Admin Notes</h2>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-pitch-green"
              placeholder="Internal notes..."
            />
            <Button onClick={handleNotesSave} disabled={saving} loading={saving} size="sm" className="mt-3">
              Save Notes
            </Button>
          </div>

          {/* Linked order */}
          {submission.orderId && (
            <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Linked Order</h2>
              <p className="text-gray-400 mb-2">
                Plan: {submission.orderId?.planName || 'N/A'} · Amount: ₹{submission.orderId?.amount ?? 'N/A'} · Payment: {submission.orderId?.paymentStatus ?? 'N/A'}
              </p>
              <Button onClick={() => navigate(`/admin/orders/${typeof submission.orderId === 'object' ? submission.orderId?._id : submission.orderId}`)} variant="outline" size="sm">
                View Order →
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </AdminLayout>
  )
}
